import { Link } from "@tanstack/react-router";
import { forwardRef } from "react";
import type { Thread } from "@/entities/thread";
import { resolveThreadLinkTarget } from "@/features/catalog/utils/threadLink";

type CatalogThreadLinkProps = Omit<
  React.AnchorHTMLAttributes<HTMLAnchorElement>,
  "href"
> & {
  thread: Pick<Thread, "id" | "b">;
};

/**
 * カタログタイルのスレリンク。単板カタログでは従来どおりrouter遷移、
 * 総合カタログでは取得元板slug付きのスレページ(`/{slug}/thread/{id}`)へ
 * router遷移する。板マウントで`b`付き(履歴の全板合算)のスレだけが
 * 板ネイティブへのanchor遷移になる。
 *
 * 本番ビルドはreactをpreact/compatへ差し替えており、Preact 10は関数
 * componentへのref-as-propを実装していない(refをpropsへ戻すのは
 * forwardRef/memo/lazyのみ)。素の関数componentのままだと呼び出し元の
 * refにanchorではなくPreactの内部instanceが入るため、必ずforwardRefで
 * 受けて実anchorへ渡す。
 */
export const CatalogThreadLink: React.ForwardRefExoticComponent<
  CatalogThreadLinkProps & React.RefAttributes<HTMLAnchorElement>
> = forwardRef(function CatalogThreadLink(
  { thread, children, ...anchorProps }: CatalogThreadLinkProps,
  ref: React.Ref<HTMLAnchorElement>,
): React.ReactNode {
  const target = resolveThreadLinkTarget(thread);
  if (target.kind === "board") {
    return (
      <a ref={ref} href={target.href} {...anchorProps}>
        {children}
      </a>
    );
  }
  if (target.kind === "mix-router") {
    return (
      <Link
        ref={ref}
        to="/$slug/thread/$threadId"
        params={{ slug: target.slug, threadId: target.threadId }}
        {...anchorProps}
      >
        {children}
      </Link>
    );
  }
  return (
    <Link
      ref={ref}
      to="/thread/$threadId"
      params={{ threadId: target.threadId }}
      {...anchorProps}
    >
      {children}
    </Link>
  );
});
